
import styled, { useTheme } from "styled-components";
import { Bar, BarChart, CartesianGrid, Label, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { useEffect, useState } from "react";
import { SwitchRow } from 'components/ui/controls/SwitchRow';
import { getISOWeek } from "utilities/dateAndTime";
import { MaterialIcon } from "components/ui/shared/MaterialIcon";

export const ProjectActivity = ({content, handler}) => {
    useEffect(() => {
        let today = new Date();
        const newActivity = {...content.activity};
        let lastKnownActivity = 0.01;
        for(let i = 0; i<24; i++)
            {
                let date = new Date();
                date.setDate(today.getDate() + (7 * i));
                const code = date.getFullYear() + "W" + getISOWeek(date);
                if(!newActivity[code])
                {
                    if(content.lastActivityAsFuture) newActivity[code] = lastKnownActivity;
                    else newActivity[code] = 0.001;
                }
                else
                {
                    lastKnownActivity = newActivity[code];
                }
            }

            handler({...content, activity: newActivity});
        },
    // eslint-disable-next-line
    []);

    return (
        <StyledProjectActivity>
            <div className="moduleHeader">
                <span>
                    Prosjektaktivitet
                </span>
                Definer predikert arbeidsmengde per uke.
            </div>
            <div className="activityOptions">
                <div>
                    <StyledSwitchBox
                        icon={<MaterialIcon
                            name="dashboard"
                            weight={300}
                        />}
                        title='Vis på dashboard'
                        description='Viser arbeidsmengden og aktiviteten på hovedsiden'
                        isChecked={content.displayOnDashboard}
                        onChange={(e) => handler?.(prev => ({...prev, displayOnDashboard: e.target.checked}))}
                    />
                    <StyledSwitchBox
                        icon={<MaterialIcon
                            name="vital_signs"
                            weight={300}
                        />}
                        title='Prosjektstatus styrt av aktivitet.'
                        description='Akvitiet styrer prosjektets status.'
                        isChecked={content.automaticStatusChange}
                        onChange={(e) => handler?.(prev => ({...prev, automaticStatusChange: e.target.checked}))}
                    />
                </div>
                <div>
                    <StyledSwitchBox
                        icon={<MaterialIcon
                            name="swipe_right_alt"
                            weight={300}
                        />}
                        title='Bruk siste aktivitet som fremtidig'
                        description='Sist registrert aktivitet blir brukt som fremtidig.'
                        isChecked={content.useLastActivityAsFuture}
                        onChange={(e) => handler?.(prev => ({...prev, useLastActivityAsFuture: e.target.checked}))}
                    />
                    <StyledSwitchBox
                        icon={<MaterialIcon
                            name="calculate"
                            weight={300}
                        />}
                        title='Bruk til beregning av tilgjengelighet'
                        description='Benytter registrert aktivitet til å beregne ledig kapasitet.'
                        isChecked={content.useToCalculateAvailability}
                        onChange={(e) => handler?.(prev => ({...prev, useToCalculateAvailability: e.target.checked}))}
                    />
                </div>
                
                
            </div>
            <div className="activityChart">
                <span>
                    Prosjektets arbeidsmengde per uke
                </span>
                <div className="chartContainer">
                    <ActivityChart
                        activity={content.activity}
                        handler={(e) => handler?.(prev => ({...prev, activity: e}))}
                    />
                </div>
            </div>
        </StyledProjectActivity>
    )
}

const StyledProjectActivity = styled.div`
    width: 100%;
    height: 100%;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: ${({theme}) => theme.spacing.lg};

    div.moduleHeader
    {
        
        span
        {
            display: block;
            font-weight: 600;
            font-size: ${({theme}) => theme.fontSize.lg};
            margin-bottom: ${({theme}) => theme.spacing.sm};
            color: ${({theme}) => theme.colors.neutralSecondaryForeground};
        }

        color: ${({theme}) => theme.colors.mutedForeground};
    }

    div.activityOptions
    {
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        gap: ${({theme}) => theme.spacing.md};

        > div
        {
            display: flex;
            flex: 1 0;
            flex-wrap: wrap;

            gap: ${({theme}) => theme.spacing.md};

            .squareSigmaIcon
            {
                stroke-width: 2px;
                width: ${({theme}) => theme.iconSize.sm};
                height: ${({theme}) => theme.iconSize.sm};
            }
        }
    }

    div.activityChart
    {
        display: flex;
        flex-direction: column;
        gap: ${({theme}) => theme.spacing.lg};

        span
        {
            font-weight: 500;
        }

        div.chartContainer
        {
            height: 400px;
        }
    }
`

const StyledSwitchBox = styled(SwitchRow)`
    min-width: 250px;
    flex: 1;
`

const ActivityChart = ({activity, handler}) => {
    
    const [activeIndex, setActiveIndex] = useState(null);
    const data = [];
    const today = new Date();
    for(let i = 0; i<24; i++)
    {
        const date = new Date();
        date.setDate(today.getDate() + (7 * i));
        const week = getISOWeek(date);
        const code = date.getFullYear() + "W" + week;
        data.push({week: code, weekLabel: week, activity: activity[code]});
    }

    const clickedAboveBar = (rechartsEvent) => {
        const index = Number(rechartsEvent.activeIndex);
        const code = data[index].week;
        
        activity[code] = activity[code] + 5;
        if(activity[code] > 40) activity[code] = 40;

        handler?.(activity);
    }

    const clickedBelowBar = (rechartsEvent, idx, event) => {
        event.stopPropagation();
        const code = rechartsEvent.week;

        activity[code] = activity[code] - 5;
        if(activity[code] <= 0.01) activity[code] = 0.001;

        handler?.(activity);
    }


    return (
        <ResponsiveContainer
            height='100%'
            width='100%'
            minHeight='0'
            minWidth='0'
            aspect={undefined}
        >
            <BarChart
                data={data}
                margin={{top: 30, left: 0, bottom: 20}}
                onClick={clickedAboveBar}
                style={{cursor: 'n-resize'}}
                onMouseMove={(state) => {
                    if(state?.isTooltipActive)
                    {
                        setActiveIndex(Number(state.activeTooltipIndex));
                    }
                    else
                    {
                        setActiveIndex(null);
                    }
                }}
                onMouseLeave={() => setActiveIndex(null)}
            >
                <CartesianGrid
                    vertical={false}
                    strokeDasharray={[5, 5]}
                />
                
                <XAxis
                    dataKey={'weekLabel'}
                >
                    <Label position='bottom'>Ukenummer</Label>
                </XAxis>
                <YAxis
                    padding={{bottom: 10}}
                    domain={[0, 40]}
                    dataKey={'activity'}
                    allowDecimals={false}
                    axisLine={false}
                    tickFormatter={(v) => {return Math.round(v) + "t."}}
                    tickLine={false}
                >
                </YAxis>
                <Bar
                    dataKey={'activity'}
                    onClick={clickedBelowBar}
                    shape={(props) => <CustomBar {...props} isActive={props.index === activeIndex}/>}
                />

            </BarChart>
        </ResponsiveContainer>
    )
}

const CustomBar = (props) => {
    const GlobalTheme = useTheme();
    const { x, y, width, height, isActive, value } = props;

    return (
        <g>
            <rect
                x={x}
                y={y}
                width={width}
                height={height}
                fill={isActive ? GlobalTheme.colors.accentForeground : GlobalTheme.colors.neutralPrimaryBackgorund}
                //fill={isActive ? (value < 35 ? GlobalTheme.colors.successBackground : value < 40 ? GlobalTheme.colors.warningBackground : GlobalTheme.colors.errorBackground): GlobalTheme.colors.neutralPrimaryBackgorund}
                strokeWidth={1}
                rx={6}
                ry={6}
                style={{
                    cursor: 's-resize',
                    
                }}
            />

            { isActive && value > 1 ?

                <text
                    x={x + width / 2}
                    y={y - 8}
                    textAnchor="middle"
                    fontSize={12}
                    fontWeight={600}
                    fill={GlobalTheme.colors.neutralPrimaryBackgorund}
                >
                    {Math.round(value) + "t."}
                </text>
            
            : null
        
            }
        </g>
    );
};